export function toPositive(number: number): number {
  return Math.abs(number);
}

export function toNegative(number: number): number {
  return -Math.abs(number);
}

const numberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export const toCommaSeparated = (v: number): string =>
  numberFormatter.format(v);

const roundNumberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const toRoundedCommaSeparated = (v: number): string =>
  roundNumberFormatter.format(v);

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export const toCurrency = (v: number): string => currencyFormatter.format(v);

const roundCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const toRoundedCurrency = (v: number): string =>
  roundCurrencyFormatter.format(v);

// formats phone number string to +1 (123) 456-7890 format
export const formatPhoneNumberOnType = (value: string) => {
  // if input value is falsy eg if the user deletes the input, then just return
  if (!value) {
    return value;
  }

  // clean the input for any non-digit values
  const phoneNumber = value.replace('+1', '').replace(/[^\d]/g, '');
  const { length } = phoneNumber;

  if (length === 0) {
    return phoneNumber;
  }

  // we need to return the value with no formatting if its less then four digits
  // this is to avoid weird behavior that occurs if you format the area code too early
  if (length < 4) {
    return `+1 (${phoneNumber}`;
  }

  // if phoneNumberLength is greater than 4 and less the 7 we start to return the formatted number
  if (length < 7) {
    return `+1 (${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }

  // if phoneNumberLength is greater than 10, we will get rid of the country code
  if (length > 10) {
    return `+1 (${phoneNumber.slice(
      length - 10,
      length - 7,
    )}) ${phoneNumber.slice(length - 7, length - 4)}-${phoneNumber.slice(
      length - 4,
      length,
    )}`;
  }

  // finally, if the phoneNumberLength is greater then seven, we add the last
  // bit of formatting and return it.
  return `+1 (${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6,
  )}-${phoneNumber.slice(6, 10)}`;
};

// formats date string to DD/MM/YYYY format
export const formatDateOnType = (value: string | null) => {
  if (!value) {
    return value;
  }

  const date = value.replace(/[^\d]/g, '');

  const dateLength = date.length;

  if (dateLength < 3) {
    return date;
  }

  if (dateLength < 5) {
    return `${date.slice(0, 2)}/${date.slice(2)}`;
  }

  if (dateLength > 5) {
    return `${date.slice(0, 2)}/${date.slice(2, 4)}/${date.slice(4, 8)}`;
  }

  return `${date.slice(0, 2)}/${date.slice(2, 4)}/${date.slice(4)}`;
};

// formats social security number string to XXX-XX-XXXX format
export const formatSSNOnType = (value: string | null) => {
  if (!value) {
    return value;
  }

  const ssn = value.replace(/[^\d]/g, '');

  const ssnLength = ssn.length;

  if (ssnLength < 4) {
    return ssn;
  }

  if (ssnLength < 6) {
    return `${ssn.slice(0, 3)}-${ssn.slice(3)}`;
  }

  if (ssnLength > 6) {
    return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5, 9)}`;
  }

  return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5)}`;
};

export function formatBytes(bytes: number, fractionDigits = 2) {
  if (bytes == 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return (
    parseFloat((bytes / Math.pow(k, i)).toFixed(fractionDigits)) +
    ' ' +
    sizes[i]
  );
}

/**
 * Returns a compact representation of a number, with a suffix indicating the magnitude.
 */
export function toCompact(number: number) {
  // Define the suffixes and their corresponding divisors
  const suffixes = ['', 'K', 'M', 'B', 'T'];
  const divisor = 1000;

  // Determine the appropriate suffix
  let suffixIndex = 0;

  while (Math.abs(number) >= divisor && suffixIndex < suffixes.length - 1) {
    number /= divisor;
    suffixIndex++;
  }

  // Format the number with the suffix
  return `${number.toFixed(0)}${suffixes[suffixIndex]}`;
}
